import React, { FC, useEffect, useState } from 'react'
import { styles } from './InstalledApplicationList-styles'
import {
    IInstalledApplicationListProps,
    IAppInfo,
    IServerAppInfo,
    IGetInstalledApplicationListRequest,
    IGetInstalledApplicationListResponse,
    TGetInstalledApplicationListStatus,
} from './InstalledApplicationList-types'
import Card from 'blocks.simple/card/card'
import translate from 'core/translate'
import Icon from 'blocks.simple/icon/icon'
import { api } from 'core/api/ConnectionManager'
import LoaderLazy from 'blocks.app/loader/_lazy/loader__lazy'
import { Typography } from 'atoms/Typography'
import { CustomScroll } from 'atoms/CustomScroll'
import { useDispatch } from 'react-redux'
import { displaysActions } from 'pages/displays/displays.state'
import { cn } from 'ethcss'
import Button from '../../../../blocks.simple/button/button'

export const InstalledApplicationList: FC<IInstalledApplicationListProps> = ({ device }) => {
    const dispatch = useDispatch()
    const [appList, setAppList] = useState<IAppInfo[]>([])
    const [serverAppList, setServerAppList] = useState<IServerAppInfo[]>([])
    const [status, setStatus] = useState<TGetInstalledApplicationListStatus>('idle')
    const [rootWarningModal, setRootWarningModal] = useState<any>(null)
    const [completeModal, setCompleteModal] = useState(false)

    useEffect(() => {
        if (status !== 'idle') return

        getAppInfo()
    }, [status])

    useEffect(() => {
        if (status !== 'init') return

        const listenersIdList: string[] = []

        api.addObserver<IGetInstalledApplicationListResponse>(
            'onInstalledApplicationListSended',
            onAppListSended,
            listenersIdList
        )

        return () => {
            listenersIdList.forEach((id) => api.removeObserver(id))
        }
    }, [status])

    const getAppInfo = () => {
        if (device) {
            api.send<IGetInstalledApplicationListRequest, null>('getInstalledApplicationList', {
                digitalSignageId: device.id,
            })
                .then(() => setStatus('init'))
                .catch(() => setStatus('error'))

            api.send<{}, IServerAppInfo[]>('getUploadManagerApps', {}).then((res) => {
                if (res) {
                    setServerAppList(res)
                }
            })
        }
    }

    const isCurrentDevice = (id: number, deviceId: number) => id === deviceId

    const onAppListSended = (response: IGetInstalledApplicationListResponse) => {
        if (device) {
            if (!isCurrentDevice(response.digitalSignageId, device.id)) return

            setAppList(response.installedApplicationList)
            setStatus('success')
        }
    }

    const handleClose = () => dispatch(displaysActions.closeModal())

    const renderAppList = () => {
        return (
            <CustomScroll classNames={styles.installedApplicationList__content}>
                <div className={styles.appsListContainer}>
                    <table className={styles.appsListTable}>
                        {appList.map((app) => {
                            return (
                                <tr>
                                    <td className={styles.appsListTableCell}>{app.name}</td>
                                    <td className={styles.appsListTableCell}></td>
                                    <td className={styles.appsListTableCell}>
                                        {translate('version') + ': ' + app.version}
                                    </td>
                                </tr>
                            )
                        })}
                    </table>
                </div>
            </CustomScroll>
        )
    }

    const renderLoader = () => {
        return (
            <div className={styles.installedApplicationList__loader}>
                <LoaderLazy active />
            </div>
        )
    }

    const renderError = () => {
        return (
            <Typography type="text" className={styles.installedApplicationList__error}>
                {translate('installedApplicationListError')}
            </Typography>
        )
    }

    const renderContent = () => {
        switch (status) {
            case 'success':
                return renderAppList()
            case 'error':
                return renderError()
            default:
                return renderLoader()
        }
    }

    const getTitle = () => translate('installedApplicationListTitle')

    const getAppStatus = (app: any) => {
        const existedApp = appList.find((item) => {
            return item.package === app.appPackage
        })

        if (!existedApp) return 'install'

        return existedApp.version === app.appVersion ? 'noNeedToUpdate' : 'update'
    }

    const onHandleUpdateApp = (id: number) => {
        if (!device) return

        const data: any = {
            digitalSignageId: device.id,
            appId: id,
        }

        if (!device.rootEnabled) {
            setRootWarningModal(data)

            return
        }

        updateApp(data)
    }

    const noRootClose = () => {
        setRootWarningModal(null)
    }

    const noRootConfirm = (data: any) => {
        updateApp(data)
        noRootClose()
    }

    const updateApp = (data: any) => {
        api.send('updateDSApp', data).then(() => setCompleteModal(true))
    }

    return (
        <div className={styles.installedApplicationList}>
            <Card
                title={getTitle()}
                titleClassName={styles.installedApplicationList__title}
                className={styles.installedApplicationList__card}
                icon={{
                    type: 'display',
                    size: 20,
                    default: true,
                }}
            >
                <div className={styles.installedApplicationList__body}>
                    <div className={styles.installedApplicationList__close} onClick={handleClose}>
                        <Icon type="close" size="20" />
                    </div>
                    <div>
                        <div className={styles.refreshWrapper}>
                            <Icon
                                type={'refresh'}
                                size={16}
                                tooltip={{ title: translate('update') }}
                                onClick={getAppInfo}
                            />
                        </div>
                        <div className={styles.infoText}>{translate('availableAppToUpdate')}</div>
                    </div>
                    <div className={cn(styles.appsListWrapper)}>
                        <div className={styles.listTitle}>{translate('serverAvailableApps')}:</div>
                        <div className={cn(styles.scrollBlockWrapper, styles.listGap)}>
                            <CustomScroll classNames={styles.installedApplicationList__content}>
                                <div className={styles.appsListContainer}>
                                    <table className={styles.appsListTable}>
                                        {serverAppList.map((app) => {
                                            const status = getAppStatus(app)

                                            return (
                                                <tr key={app.id}>
                                                    <td className={styles.appsListTableCell}>{app.appName}</td>
                                                    <td
                                                        className={cn(
                                                            styles.appsListTableCell,
                                                            status === 'update' || status === 'install'
                                                                ? styles.activeAction
                                                                : ''
                                                        )}
                                                        onClick={() => {
                                                            if (status === 'noNeedToUpdate') return

                                                            onHandleUpdateApp(app.id)
                                                        }}
                                                    >
                                                        {translate(status)}
                                                    </td>
                                                    <td className={styles.appsListTableCell}>
                                                        {translate('version') + ': ' + app.appVersion}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </table>
                                </div>
                            </CustomScroll>
                        </div>
                        <div className={styles.listTitle}>{translate('deviceAvailableApps')}:</div>
                        <div className={styles.scrollBlockWrapper}>{renderContent()}</div>
                    </div>
                </div>
            </Card>
            {rootWarningModal && (
                <div className={styles.modal}>
                    <div className={styles.modalInner}>
                        <div>
                            {translate('noRootInstall')}
                            <br />
                            <br />
                            {translate('needToApprove')}
                        </div>
                        <div className={styles.controls}>
                            <Button mod={'withBorder'} className={styles.button} onClick={noRootClose}>
                                {translate('cancel')}
                            </Button>
                            <Button
                                mod={'fill'}
                                className={styles.button}
                                onClick={() => noRootConfirm(rootWarningModal)}
                            >
                                {translate('continue')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {completeModal && (
                <div className={styles.modal}>
                    <div className={styles.modalInner}>
                        <div>
                            {translate('requestIsSent')}
                            <br />
                            <br />
                            {translate('fewMinutesUpdate')}
                        </div>
                        <div className={styles.controls}>
                            <Button mod={'fill'} className={styles.button} onClick={handleClose}>
                                {translate('close')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
